import { memo, useEffect, useId, useRef, type PointerEvent } from 'react';
import { Spin } from 'antd';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import type { ChartModel, DeviceLine } from '../lib/chartModel';
import type { ShippingEvent } from '../lib/events';
import type { Extent } from '../lib/extent';
import {
  CHART_MARGIN,
  PLOT_HEIGHT,
  Y_LABEL_GAP,
  type LinearScale,
  type XTick,
  type YAxis,
} from '../lib/layout';
import { scrollToTop } from '../lib/scroll';
import type { Origin } from '../lib/selection';
import { ChunkStatus } from '../queries';
import { useDoubleTap } from '../useDoubleTap';
import RangeHandles, { type RangeHandlesProps } from './RangeHandles';
import styles from './VitalChart.module.css';

const NOT_LOADED_LABEL_MIN_PX = 80;
const NOT_LOADED_LABEL_TOP = 20;

const STATUS_MESSAGES: Record<
  ChunkStatus.Failed | ChunkStatus.TooLarge,
  string
> = {
  [ChunkStatus.Failed]: "Couldn't load these readings.",
  [ChunkStatus.TooLarge]: 'Too many readings to show.',
};

/** Props for {@link VitalChart}. */
export interface VitalChartProps {
  model: ChartModel;
  status: ChunkStatus;
  width: number;
  marginLeft: number;
  xScale: LinearScale;
  xTicks: readonly XTick[];
  yAxis: YAxis;
  marked: boolean;
  selection: Extent;
  notLoaded: readonly Extent[];
  origins: readonly Origin[];
  labelOrigins: boolean;
  note: string | null;
  handles: Omit<RangeHandlesProps, 'xScale' | 'height'> | null;
  onZoomOut: (() => void) | null;
  hoverAt: number | null;
  events: readonly ShippingEvent[];
  activeEventId: string | null;
  onHover: (event: PointerEvent<SVGSVGElement>) => void;
  onHoverEnd: () => void;
  focused?: boolean;
  onFocused?: () => void;
}

interface SeriesLayerProps {
  lines: readonly DeviceLine[];
  xScale: LinearScale;
  yScale: LinearScale;
}

const indexesOf = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

const SeriesLayer = memo(({ lines, xScale, yScale }: SeriesLayerProps) => (
  <>
    {lines.map(({ device, t, v, segments }) =>
      segments.map(([start, end]) => {
        if (start === end) {
          return (
            <circle
              key={`${device.id}-${t[start]}`}
              cx={xScale(t[start])}
              cy={yScale(v[start])}
              r={2.5}
              fill={device.color}
            />
          );
        }
        return (
          <LinePath<number>
            key={`${device.id}-${t[start]}`}
            data={indexesOf(start, end)}
            x={(i) => xScale(t[i])}
            y={(i) => yScale(v[i])}
            stroke={device.color}
            strokeWidth={2}
          />
        );
      })
    )}
  </>
));
SeriesLayer.displayName = 'SeriesLayer';

/**
 * One measure's chart: y gridlines and labels, x labels, one line per visible device, and "no data" notes.
 * When `marked`, readings outside the selection are faded, spans that failed to load are shaded, and the
 * selection gets draggable handles (or plain lines when the trip is too short for them). Zoomed, a
 * double-click or double-tap anywhere on the plot calls `onZoomOut`. A crosshair marks `hoverAt`, and thin grey lines
 * mark `events`, the hovered one bold; the stack shares both across every chart. When `focused`, it scrolls itself
 * to the top of the stack, takes focus and calls `onFocused`.
 */
const VitalChart = ({
  model,
  status,
  width,
  marginLeft,
  xScale,
  xTicks,
  yAxis,
  marked,
  selection,
  notLoaded,
  origins,
  labelOrigins,
  note,
  handles,
  onZoomOut,
  hoverAt,
  events,
  activeEventId,
  onHover,
  onHoverEnd,
  focused = false,
  onFocused,
}: VitalChartProps) => {
  const { measure, lines, missing } = model;
  const plotRight = width - CHART_MARGIN.right;
  const height = CHART_MARGIN.top + PLOT_HEIGHT + CHART_MARGIN.bottom;
  const clipId = useId();
  const zoomOut = useDoubleTap(() => onZoomOut?.());
  const boundsX: Extent = [xScale(selection[0]), xScale(selection[1])];
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!focused) return;
    if (!sectionRef.current) return;
    scrollToTop(sectionRef.current);
    sectionRef.current.focus({ preventScroll: true });
    onFocused?.();
  }, [focused, onFocused]);

  return (
    <section
      ref={sectionRef}
      className={styles.chart}
      aria-label={`${measure.label} chart`}
      tabIndex={-1}
    >
      <div className={styles.titleRow}>
        <div className={styles.titleGroup}>
          <h3 className={styles.title}>{measure.label}</h3>
          {status === ChunkStatus.Ready &&
            missing.map((device) => (
              <span key={device.id} className={styles.missing}>
                <span
                  className={styles.missingSwatch}
                  // eslint-disable-next-line react/forbid-dom-props -- the device colour is a runtime value
                  style={{ backgroundColor: device.color }}
                />
                No data from {device.label}
              </span>
            ))}
        </div>
        {note && <span className={styles.titleNote}>{note}</span>}
      </div>
      {labelOrigins && (
        <div className={styles.originLabels} data-testid='origin-labels'>
          {origins.map((origin) => (
            <span
              key={origin.label}
              className={styles.originLabel}
              // eslint-disable-next-line react/forbid-dom-props -- the label's x position is a runtime value
              style={{ left: xScale(origin.at) }}
            >
              {origin.label}
            </span>
          ))}
        </div>
      )}
      <div className={styles.plot}>
        <svg
          width={width}
          height={height}
          role='group'
          aria-label={measure.label}
          className={onZoomOut ? styles.zoomed : undefined}
          onPointerDown={(event) => {
            onHover(event);
            if (onZoomOut) zoomOut.down(event);
          }}
          onPointerMove={onHover}
          onPointerLeave={onHoverEnd}
          onPointerUp={onZoomOut ? zoomOut.up : undefined}
        >
          <Group top={CHART_MARGIN.top}>
            {marked &&
              notLoaded.map(([from, to]) => {
                const x = xScale(from);
                const spanWidth = Math.max(0, xScale(to) - x);
                return (
                  <g key={from} className={styles.notLoaded}>
                    <rect x={x} y={0} width={spanWidth} height={PLOT_HEIGHT} />
                    {spanWidth >= NOT_LOADED_LABEL_MIN_PX && (
                      <text
                        x={x + spanWidth / 2}
                        y={NOT_LOADED_LABEL_TOP}
                        textAnchor='middle'
                        dominantBaseline='middle'
                      >
                        Not loaded
                      </text>
                    )}
                  </g>
                );
              })}
            {yAxis.ticks.map((tick) => (
              <g key={tick.value}>
                <line
                  className={styles.grid}
                  x1={marginLeft}
                  x2={plotRight}
                  y1={yAxis.scale(tick.value)}
                  y2={yAxis.scale(tick.value)}
                />
                <text
                  className={styles.axisLabel}
                  x={marginLeft - Y_LABEL_GAP}
                  y={yAxis.scale(tick.value)}
                  textAnchor='end'
                  dominantBaseline='middle'
                >
                  {tick.label}
                </text>
              </g>
            ))}
            {status === ChunkStatus.Ready && !marked && (
              <SeriesLayer lines={lines} xScale={xScale} yScale={yAxis.scale} />
            )}
            {status === ChunkStatus.Ready && marked && (
              <>
                <defs>
                  <clipPath id={clipId}>
                    <rect
                      x={boundsX[0]}
                      y={-CHART_MARGIN.top}
                      width={Math.max(0, boundsX[1] - boundsX[0])}
                      height={CHART_MARGIN.top + PLOT_HEIGHT}
                    />
                  </clipPath>
                </defs>
                <g className={styles.faded} data-testid='faded-series'>
                  <SeriesLayer
                    lines={lines}
                    xScale={xScale}
                    yScale={yAxis.scale}
                  />
                </g>
                <g
                  clipPath={`url(#${clipId})`}
                  data-testid='operational-series'
                >
                  <SeriesLayer
                    lines={lines}
                    xScale={xScale}
                    yScale={yAxis.scale}
                  />
                </g>
              </>
            )}
            {origins.map((origin) => (
              <line
                key={origin.label}
                className={styles.origin}
                data-testid='origin-line'
                x1={xScale(origin.at)}
                x2={xScale(origin.at)}
                y1={0}
                y2={PLOT_HEIGHT}
              />
            ))}
            {marked && !handles && (
              <>
                <line
                  className={styles.bound}
                  data-testid='operational-bound'
                  x1={boundsX[0]}
                  x2={boundsX[0]}
                  y1={0}
                  y2={PLOT_HEIGHT}
                />
                <line
                  className={styles.bound}
                  data-testid='operational-bound'
                  x1={boundsX[1]}
                  x2={boundsX[1]}
                  y1={0}
                  y2={PLOT_HEIGHT}
                />
              </>
            )}
            {events.map((event) => (
              <line
                key={event.id}
                className={
                  event.id === activeEventId ? styles.eventActive : styles.event
                }
                data-testid='event-line'
                x1={xScale(event.at)}
                x2={xScale(event.at)}
                y1={0}
                y2={PLOT_HEIGHT}
              />
            ))}
            {hoverAt !== null && (
              <line
                className={styles.crosshair}
                data-testid='crosshair'
                x1={xScale(hoverAt)}
                x2={xScale(hoverAt)}
                y1={0}
                y2={PLOT_HEIGHT}
              />
            )}
            {handles && (
              <RangeHandles {...handles} xScale={xScale} height={PLOT_HEIGHT} />
            )}
          </Group>
          {xTicks.map((tick) => (
            <text
              key={tick.at}
              className={styles.axisLabel}
              x={xScale(tick.at)}
              y={height - 6}
              textAnchor={tick.anchor}
            >
              {tick.label}
            </text>
          ))}
        </svg>
        {status !== ChunkStatus.Ready && (
          <div className={styles.overlay} role='status'>
            {status === ChunkStatus.Loading ? (
              <Spin />
            ) : (
              STATUS_MESSAGES[status]
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default VitalChart;
